// A calibration run: 20 seconds of tapping to a click (calibration.ts has the click track and the fit). The taps are on the context clock,
// the same heard-time axis the clicks use. The run lives here, not in the sheet, so closing the sheet cancels it and the keys can tap into it.

import { CLICKS, CLICK_LEAD_S, CLICK_PERIOD_S, WINDOW_S, fitCalibration, scheduleClicks } from '../calibration.js';
import { engine } from '../engine/engine.js';
import { createStore } from './store.js';

interface Run { t0: number; taps: number[]; bus: GainNode; timer: number }

export interface CalibrationView {
  readonly running: boolean;
  readonly taps: number;
  /** The result of the last run, as a sentence; null before one. */
  readonly result: string | null;
}

export const calibration = createStore<CalibrationView>({ running: false, taps: 0, result: null });

let run: Run | null = null;

/** Describe an offset in words (DESIGN §2: "Your taps land 34 ms late on this device. Saved."). */
export function describeOffset(offsetS: number): string {
  const ms = Math.round(offsetS * 1000);
  if (Math.abs(ms) < 3) return 'Your taps land on time on this device.';
  return `Your taps land ${Math.abs(ms)} ms ${ms > 0 ? 'late' : 'early'} on this device.`;
}

/** A tap during a run goes to the run. Returns false when none is running. */
export function calibrationTap(): boolean {
  if (!run) return false;
  run.taps.push(engine.now() - engine.calibrationBaseline); // against the delay without any calibration in it
  calibration.patch({ taps: run.taps.length });
  return true;
}

export async function startCalibration(): Promise<void> {
  if (run) return;
  engine.pause();
  await engine.ctx.resume();
  const t0 = engine.now() + CLICK_LEAD_S;
  const bus = scheduleClicks(engine.ctx, t0);
  const timer = window.setTimeout(() => void finishCalibration(false), (CLICK_LEAD_S + CLICKS * CLICK_PERIOD_S + WINDOW_S) * 1000);
  run = { t0, taps: [], bus, timer };
  calibration.set({ running: true, taps: 0, result: null });
}

/** End the run: `cancelled` drops it, otherwise the taps are fitted and the result kept (and saved by the engine). */
export async function finishCalibration(cancelled: boolean): Promise<void> {
  const done = run;
  if (!done) return;
  run = null;
  window.clearTimeout(done.timer);
  done.bus.disconnect();
  if (cancelled) {
    calibration.set({ running: false, taps: 0, result: null });
    return;
  }
  const fit = fitCalibration(done.taps, done.t0);
  if (!fit) {
    calibration.set({ running: false, taps: 0, result: 'Too few taps to measure. Tap on each click and try again.' });
    return;
  }
  await engine.setCalibration(fit.offsetS, fit.valid);
  const saved = engine.getState().calibrationSaved;
  calibration.set({
    running: false, taps: 0,
    result: `${describeOffset(fit.offsetS)} ${saved ? 'Saved.' : 'Not saved: this tab keeps it until you close it.'}`,
  });
}
